// Sprint 4B — Admin Arbitration Case Builder.
// Pick an analyzed credit profile, click "Build Case", get a PDF download.
import { useCallback, useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { apiCall } from "../auth/AuthContext";
import { Loader2, Gavel, Download, AlertCircle, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const STATUS_FILTERS = ["all", "pending", "ready", "error"];

export default function AdminArbitration() {
  const [profiles, setProfiles] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(null);
  const [msg, setMsg] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const reload = useCallback(async (filt) => {
    const q = (filt || statusFilter) === "all" ? "" : `?status=${filt || statusFilter}`;
    const [p, c] = await Promise.all([
      apiCall("/api/admin/credit/queue").catch(() => ({ records: [] })),
      apiCall(`/api/admin/arbitration/cases${q}`).catch(() => ({ records: [] })),
    ]);
    return { profiles: p.records || [], cases: c.records || [] };
  }, [statusFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const d = await reload(statusFilter);
      if (cancelled) return;
      setProfiles(d.profiles); setCases(d.cases); setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reload, statusFilter]);

  const hideCase = async (caseId) => {
    try {
      await apiCall(`/api/admin/arbitration/cases/${caseId}`, { method: "DELETE" });
      const fresh = await reload(statusFilter);
      setCases(fresh.cases);
    } catch (e) {
      setMsg(`× Hide failed: ${e.message || ""}`);
    }
  };

  const build = async (profileId) => {
    setBuilding(profileId); setMsg("");
    try {
      const d = await apiCall(`/api/admin/arbitration/build/${profileId}`, { method: "POST" });
      setMsg(`✔ Case ${d.case.case_id} queued. Building in the background — polling for completion…`);
      const initial = await reload(statusFilter);
      setCases(initial.cases);
      const targetId = d.case.id;
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const fresh = await reload(statusFilter);
        setCases(fresh.cases);
        const found = (fresh.cases || []).find((c) => c.id === targetId);
        if (found && found.status === "ready") {
          setMsg(`✔ Case ${d.case.case_id} ready. PDF below.`);
          break;
        }
        if (found && found.status === "error") {
          setMsg(`× Build failed: ${found.error_message || "unknown error"}`);
          break;
        }
      }
    } catch (e) {
      setMsg(`× ${e.message || "Build failed"}`);
    } finally { setBuilding(null); }
  };

  const downloadPdf = async (caseDocId, caseId) => {
    const token = localStorage.getItem("jc3_token");
    const res = await fetch(`${API}/api/admin/arbitration/cases/${caseDocId}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { setMsg(`× Download failed (${res.status})`); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${caseId || "case"}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="ARBITRATION CASE BUILDER" kicker="Sprint 4B · 7E · Legal-review handoff">
      <div className="mb-6 border border-jc-gold/25 bg-jc-navy/30 px-5 py-4 rounded text-sm text-jc-muted flex items-start gap-3" data-testid="arbitration-disclaimer">
        <AlertCircle size={18} className="text-jc-gold shrink-0 mt-0.5"/>
        <p>Case files are <span className="text-jc-white">automated drafts</span> for outside legal counsel review. JC3 Wealth Legacy Group LLC does not provide legal representation. Always have your retained attorney review before any filing.</p>
      </div>

      {loading ? <div className="py-16 flex items-center justify-center text-jc-muted gap-3"><Loader2 className="animate-spin" size={18}/> Loading…</div> :
       <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 space-y-2" data-testid="arbitration-profile-list">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold mb-2">◆ Eligible profiles</p>
            {profiles.map((p) => (
              <div key={p.id} className="border border-jc-gold/15 px-4 py-3" data-testid={`arb-profile-${p.id}`}>
                <p className="font-display text-jc-white">{p.client_first_name || p.client_email}</p>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-jc-muted mt-0.5">
                  {(p.profile?.dispute_candidates || []).filter((c) => (c.severity || "").toLowerCase() === "high").length} high-severity candidates
                </p>
                <button data-testid={`arb-build-${p.id}`} disabled={building === p.id} onClick={() => build(p.id)}
                  className="mt-3 jc-btn jc-btn-primary !text-[11px] !py-2 disabled:opacity-50">
                  {building === p.id ? <><Loader2 size={12} className="animate-spin"/> Building…</> : <><Gavel size={12}/> Build case</>}
                </button>
              </div>
            ))}
            {profiles.length === 0 && <p className="text-sm text-jc-muted">No profiles yet.</p>}
          </div>

          <div className="lg:col-span-7 space-y-3" data-testid="arbitration-case-list">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold">◆ Generated cases</p>
              <div className="flex gap-1" data-testid="arb-status-filter">
                {STATUS_FILTERS.map((s) => (
                  <button key={s} onClick={() => setStatusFilter(s)} data-testid={`arb-filter-${s}`}
                    className={`px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] border ${statusFilter === s ? "border-jc-gold bg-jc-gold/10 text-jc-gold" : "border-jc-gold/20 text-jc-muted"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            {msg && <p className={`text-sm ${msg.startsWith("✔") ? "text-emerald-300" : "text-red-300"}`} data-testid="arbitration-msg">{msg}</p>}
            {cases.length === 0 ? <p className="text-sm text-jc-muted">No cases built yet.</p> :
              cases.map((c) => (
                <div key={c.id} className="border border-jc-gold/20 bg-jc-navy/40 p-5 rounded-xl" data-testid={`arb-case-${c.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold">◆ {c.case_id}</p>
                      <p className="font-display text-jc-white mt-1">{c.client_first_name || c.client_email}</p>
                      <p className="text-xs font-mono uppercase tracking-[0.18em] text-jc-muted mt-1">
                        {new Date(c.created_at).toLocaleString()} · {c.unresolved_count} items · {c.status === "ready" ? `${(c.pdf_size_bytes / 1024).toFixed(0)} KB` : c.status}
                      </p>
                    </div>
                    {c.status === "ready" ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => downloadPdf(c.id, c.case_id)} className="jc-btn jc-btn-primary !text-xs" data-testid={`arb-download-${c.id}`}>
                          <Download size={12}/> Download PDF
                        </button>
                        <button onClick={() => hideCase(c.id)} title="Hide case" className="jc-btn jc-btn-outline !text-xs !px-2.5" data-testid={`arb-hide-${c.id}`}>
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    ) : c.status === "error" ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono uppercase tracking-[0.22em] text-red-300">× Failed</span>
                        <button onClick={() => hideCase(c.id)} title="Hide error" className="jc-btn jc-btn-outline !text-xs !px-2.5" data-testid={`arb-hide-${c.id}`}>
                          <Trash2 size={12}/>
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs font-mono uppercase tracking-[0.22em] text-jc-muted flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Building…</span>
                    )}
                  </div>
                  {c.damages_estimate && <p className="mt-3 text-sm text-jc-white"><span className="text-jc-gold">Damages estimate:</span> {c.damages_estimate}</p>}
                  {(c.violations_summary || []).length > 0 && (
                    <details className="mt-3"><summary className="text-xs font-mono uppercase tracking-[0.18em] text-jc-muted cursor-pointer">Violations cited ({c.violations_summary.length})</summary>
                      <ul className="mt-2 text-xs text-jc-muted space-y-1">{c.violations_summary.map((v, i) => <li key={i}>• {v}</li>)}</ul>
                    </details>
                  )}
                </div>
              ))
            }
          </div>
        </div>
      }
    </AdminLayout>
  );
}
